import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import ChartTitle from '../Tabs/ChartTitle';

/**
 * Componente padrão de gráfico de barras para exibir dados agrupados.
 * 
 * @param {Object} props
 * @param {string} props.titulo - Título exibido acima do gráfico
 * @param {Array<string>} props.labels - Rótulos do eixo X
 * @param {Array<number>} props.data - Valores correspondentes aos rótulos
 * @param {string} [props.cor='#6c63ff'] - Cor da barra (opcional)
 */
const GraficoBarrasPadrao = ({
  titulo = 'Leads por etapa', // Título padrão se nenhum for fornecido
  labels = [],                // Array de strings para o eixo X
  data = [],                  // Array de valores numéricos para as barras
  cor = '#6c63ff',            // Cor padrão das barras (roxo)
  legendaY = 'Sua Legenda'
}) => {
  const theme = useTheme();
  const axisTextColor = theme.palette.text.secondary;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 200,
        mx: 'auto',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        width: '100%',
      }}
    >
      {/* Label do eixo Y - rotacionado no desktop e normal no mobile */}
      <Typography
        variant="body2"
        sx={{
          transform: { xs: 'none', sm: 'rotate(-90deg)' }, // Rotaciona o texto em 90° em telas maiores
          whiteSpace: 'nowrap',             // Evita quebra de linha no texto
          mb: { xs: 1, sm: 0 },             // Margem inferior no mobile
          mr: { xs: 0, sm: -6 },            // Ajuste de margem direita no desktop
          ml: { xs: 0, sm: -6 },            // Ajuste de margem esquerda no desktop
          fontSize: { xs: '14px', sm: '16px' }, // Tamanho de fonte responsivo
          textAlign: 'center',              // Centraliza o texto
        }}
      >
        {legendaY}
      </Typography>

      {/* Linha divisória entre o label Y e o gráfico */}
      <Box
        sx={{
          width: { xs: '100%', sm: '1px' },  // Linha horizontal no mobile, vertical no desktop
          height: { xs: '1px', sm: '100%' }, // Altura ajustada conforme o layout
          backgroundColor: theme.palette.divider, // Cor cinza claro semi-transparente
          mb: { xs: 1, sm: 0 },              // Margem inferior no mobile
        }}
      />

      {/* Container do gráfico */}
      <Box
        aria-label={`Gráfico de barras: ${titulo}`}
        sx={{
          flex: 1,
          width: '100%',
          minWidth: 0, // ESSENCIAL para evitar colapso em layout com flex
        }}
      >
        {/* Componente de título do gráfico (importado de ../Tabs/ChartTitle) */}
        <ChartTitle>{titulo}</ChartTitle>

        {/* Componente de gráfico de barras do MUI X Charts */}
        <BarChart
          // Configuração da série de dados (apenas uma série neste caso)
          series={[{
            data,                // Array de valores para as barras
            color: cor,          // Cor personalizada das barras
            showLabel: true,     // Mostra valores nas barras
          }]}

          // Configura a exibição dos rótulos nas barras (oculta em barras pequenas)
          barLabel={(item, context) =>
            context.bar.height < 10 ? null : item.value?.toString()
          }

          // Configuração do eixo X
          xAxis={[{
            id: 'x-axis',
            data: labels,           // Rótulos do eixo X
            scaleType: 'band',      // Tipo de escala para dados categóricos
            paddingInner: 0.5,      // Espaço entre as barras (50%)
            paddingOuter: 0.3,      // Espaço nas bordas externas (30%)
            tickLabelStyle: {
              fontSize: 12,         // Tamanho da fonte dos rótulos
              fill: axisTextColor,         // Cor dos rótulos (cinza escuro)
              whiteSpace: 'normal', // Permite quebra de linha em rótulos longos
            },
            tickSize: 0,            // Remove os traços de marcação
            label: '',              // Remove o label do eixo X (usamos personalizado)
          }]}

          // Configuração do eixo Y
          yAxis={[{
            id: 'y-axis',
            tickLabelStyle: {
              fontSize: 12,         // Tamanho da fonte dos valores
              fill: axisTextColor,         // Cor dos valores (cinza escuro)
            },
            tickSize: 0,            // Remove os traços de marcação
            label: '',              // Remove o label padrão do eixo Y (usamos personalizado)
          }]}

          // Remove as linhas de grade
          grid={{ horizontal: false, vertical: false }}

          // Margens do gráfico dentro do container
          margin={{ top: 20, right: 30, bottom: 40, left: 50 }}

          // Altura fixa do gráfico
          height={250}

          // Arredondamento das bordas do container do gráfico
          borderRadius={10}

          // Personalização avançada dos elementos do gráfico
          components={{
            Bar: (props) => <rect {...props} rx={15} ry={15} />, // Personaliza as barras com bordas arredondadas
          }}

          // Estilização adicional via SX prop
          sx={{
            '& .MuiBarElement-root': {
              borderRadius: '8px',  // Arredonda as bordas das barras
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default GraficoBarrasPadrao;
